import { capitalCase } from "text-case";
import {
  ControlledComboBox,
  ControlledDataGrid,
  ControlledDataGridColumnProps,
  ControlledDatePicker,
  ControlledInput,
  ControlledTextArea,
  FormFor,
  FormSection,
} from "@excent-one/react/form";
import { ContentLayout, ContentHeader } from "@excent-one/react/layout/content";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notification } from "@excent-one/react/notification";
import { dialogBox } from "@excent-one/react";
import FormActionsBar from "@excent-one/react/form/layouts/FormActionsBar";
import { Button, FormFieldSet } from "@progress/kendo-react-all";
import {
  cancelIcon,
  checkIcon,
  pencilIcon,
  saveIcon,
  undoIcon,
} from "@progress/kendo-svg-icons";
import {
  approveSiteScheduleChangeRequests,
  cancelSiteScheduleChangeRequests,
  getAllAreas,
  getAllBranches,
  getAllChangeRequestStatuses,
  getMyDetails,
  getMyMembers,
  getSscrSiteAssignmentById,
  reviseSiteScheduleChangeRequests,
  saveScheduleSiteChangeRequest,
  SaveScheduleSiteChangeRequest,
  ScheduleSiteChangeRequestAssignmentDto,
  ScheduleSiteChangeRequestDto,
  submitSiteScheduleChangeRequests,
} from "@phrmpc/infrastructure/phrmpc";
import { useQuery } from "@tanstack/react-query";
import { formatDate } from "@progress/kendo-intl";
import { DateTime } from "luxon";
import { useAuthContext } from "@excent-one/auth";
import { validateSchedules } from "../utils/ValidateSchedules";
import {
  DEPLOYMENT_ACTIONS,
  MONTHS_OPTIONS,
  SSCR_STATUS_OPTIONS,
  YEARS_OPTIONS,
} from "@phrmpc/shared";
import { isEmpty } from "lodash";

const DRAFT_STATUS_ID = SSCR_STATUS_OPTIONS.find((s) => s.code === "DRAFT")?.id;
const FOR_SUPERVISOR_APPROVAL_STATUS_ID = SSCR_STATUS_OPTIONS.find(
  (s) => s.code === "FOR_SUPERVISOR_APPROVAL",
)?.id;
const FOR_REVISION_STATUS_ID = SSCR_STATUS_OPTIONS.find(
  (s) => s.code === "FOR_REVISION",
)?.id;

const { Form, useFormRef } = FormFor<ScheduleSiteChangeRequestDto>();

const ScheduleAndSiteChangeRequestForm = () => {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const formRef = useFormRef();
  const [loading, setLoading] = useState(false);
  const [{ currentUser }, { isInAnyRole }] = useAuthContext();

  const location = useLocation();

  const isDisplayOnly = location.pathname.includes("/display");

  const isUserAreaHead = isInAnyRole("Area Head");
  const isUserManager = isInAnyRole("Manager");

  const minSchedDateRef = useRef<DateTime | null>(null);
  const maxSchedDateRef = useRef<DateTime | null>(null);

  const [initialValues, setInitialValues] = useState<
    ScheduleSiteChangeRequestDto | undefined
  >(undefined);
  const initialAssignmentsRef = useRef<
    ScheduleSiteChangeRequestAssignmentDto[]
  >([]);

  const { data: areaOptionsData } = useQuery({
    queryKey: ["areas"],
    queryFn: () => getAllAreas(),
  });
  const areaOptions = areaOptionsData ?? [];

  const { data: branchOptions = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: () => getAllBranches(),
  });

  const { data: statusOptions } = useQuery({
    queryKey: ["status"],
    queryFn: () => getAllChangeRequestStatuses(),
  });

  const { data: myDetails } = useQuery({
    queryKey: ["my-details", currentUser?.id],
    queryFn: () => getMyDetails(),
  });

  const payload = (
    formValues: ScheduleSiteChangeRequestDto,
  ): ScheduleSiteChangeRequestDto => {
    formValues.monthName = MONTHS_OPTIONS.find(
      (m) => m.id == formValues.monthId,
    )?.name;
    formValues.areaName = areaOptions.find(
      (a) => a.id == formValues.areaId,
    )?.name;

    const combineDateAndTime = (
      date: Date | undefined,
      time: Date | undefined,
    ) => {
      if (!date || !time) return time;
      const combined = new Date(date);
      combined.setHours(
        time.getHours(),
        time.getMinutes(),
        time.getSeconds(),
        0,
      );
      return combined;
    };

    const assignments = formValues.payload?.map((assignment) => {
      const branchName = branchOptions.find(
        (b) => b.id === assignment.branchId,
      )?.name;

      return {
        ...assignment,
        branchName,
        fromSchedule: combineDateAndTime(
          assignment.date,
          assignment.fromSchedule,
        ),
        toSchedule: combineDateAndTime(assignment.date, assignment.toSchedule),
      };
    });

    return { ...formValues, payload: assignments };
  };

  const { data: directMembers } = useQuery({
    queryKey: ["my-members", currentUser?.id],
    queryFn: () =>
      getMyMembers({
        payload: {
          pageSize: 10000,
        },
      } as any),
  });

  const isDirectMember = useMemo(() => {
    const memberIds = new Set((directMembers?.items ?? []).map((m) => m.id));
    return (coordinatorId?: string) =>
      !!coordinatorId && memberIds.has(coordinatorId);
  }, [directMembers?.items]);

  const actionAvailability = (
    button: (typeof DEPLOYMENT_ACTIONS)[keyof typeof DEPLOYMENT_ACTIONS],
    sscrSiteAssignment: ScheduleSiteChangeRequestDto,
  ) => {
    switch (button) {
      case DEPLOYMENT_ACTIONS.Save:
        return !(
          !isDisplayOnly &&
          sscrSiteAssignment.coordinatorId == myDetails?.id &&
          [DRAFT_STATUS_ID, FOR_REVISION_STATUS_ID].includes(
            sscrSiteAssignment?.statusId ?? 0,
          )
        );
      case DEPLOYMENT_ACTIONS.Submit:
      case DEPLOYMENT_ACTIONS.Cancel:
      case DEPLOYMENT_ACTIONS.Edit:
        return !(
          isDisplayOnly &&
          sscrSiteAssignment.coordinatorId == myDetails?.id &&
          [DRAFT_STATUS_ID, FOR_REVISION_STATUS_ID].includes(
            sscrSiteAssignment?.statusId ?? 0,
          )
        );
      case DEPLOYMENT_ACTIONS.ForRevision:
      case DEPLOYMENT_ACTIONS.Approve:
        return !(
          sscrSiteAssignment?.statusId == FOR_SUPERVISOR_APPROVAL_STATUS_ID &&
          ((isUserAreaHead && isDirectMember(sscrSiteAssignment.coordinatorId)) || isUserManager) &&
          sscrSiteAssignment.coordinatorId != myDetails?.id
        );
      default:
        break;
    }
  };

  const loadData = async (id: string | undefined) => {
    if (!id) return;

    const sscr: ScheduleSiteChangeRequestDto =
      await getSscrSiteAssignmentById(id);

    sscr.payload?.sort((a, b) => {
      const dateDiff = (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0);

      if (dateDiff !== 0) {
        return dateDiff;
      }

      return (
        (a.fromSchedule?.getTime() ?? 0) - (b.fromSchedule?.getTime() ?? 0)
      );
    });

    setInitialValues(sscr);
    minSchedDateRef.current = sscr.dateFrom
      ? DateTime.fromJSDate(sscr.dateFrom)
      : null;
    maxSchedDateRef.current = sscr.dateTo
      ? DateTime.fromJSDate(sscr.dateTo)
      : null;

    const sscrAssignments: ScheduleSiteChangeRequestAssignmentDto[] =
      sscr.payload ? structuredClone(sscr.payload) : [];

    initialAssignmentsRef.current = sscrAssignments;
  };

  useEffect(() => {
    const init = async () => {
      if (id) {
        await loadData(id);
      } else {
        const now = new Date();
        now.setDate(1);
        now.setMonth(now.getMonth() + 1);
        const nextMonth = now.getMonth() + 1;
        const nextYear = now.getFullYear();

        const defaultValues: ScheduleSiteChangeRequestDto = {
          coordinatorId: currentUser?.id,
          coordinatorName: currentUser?.displayName,
          monthId: nextMonth,
          monthName: now.toLocaleString("default", { month: "long" }),
          areaId: myDetails?.areaId ?? areaOptions[0]?.id,
          areaName: myDetails?.areaName ?? areaOptions[0]?.name,
          year: nextYear,
          statusId: 1,
          statusName: "Draft",
          remarks: "",
          coordinatorRemarks: "",
          supervisorRemarks: "",
          payload: [],
        };
        setInitialValues(defaultValues);
      }
    };

    init();
  }, [id, areaOptions, currentUser, myDetails]);

  const handleSave = useCallback(
    async (
      values: ScheduleSiteChangeRequestDto,
    ): Promise<ScheduleSiteChangeRequestDto> => {
      try {
        if (values.coordinatorId && values.year && values.monthId) {
          const saveScheduleSiteChangeRequestPayload: SaveScheduleSiteChangeRequest =
            {
              payload: payload(values),
            };

          if (id) {
            const newData = await saveScheduleSiteChangeRequest(
              id,
              saveScheduleSiteChangeRequestPayload,
            );

            setInitialValues(newData);
            initialAssignmentsRef.current = structuredClone(
              newData.payload ?? [],
            );

            notification.success(
              "Schedule and Site Change Request saved successfully.",
            );
          }
        } else {
          notification.error(
            "Unable to save Schedule and Site Change Request. Please try again.",
          );
        }

        return values;
      } catch (e) {
        notification.error(
          id
            ? "Unable to save Schedule and Site Change Request. Please try again."
            : "Unable to save draft. Please try again.",
        );
        throw e;
      }
    },
    [id, areaOptions, branchOptions, navigate],
  );

  const handleApprove = useCallback(
    async (values: ScheduleSiteChangeRequestDto): Promise<void> => {
      dialogBox.confirm({
        title: "Approve Schedule and Site Change Request?",
        content: (
          <>
            Are you sure you want to approve Schedule and Site Change Request?
          </>
        ),
        actions: {
          confirm: {
            text: "Yes.",
            onClick: async () => {
              try {
                await approveSiteScheduleChangeRequests(id ?? "", {
                  id: id ?? "",
                  supervisorRemarks: values.supervisorRemarks,
                });
                notification.success(
                  "Schedule and Site Change Request approved successfully.",
                );

                await loadData(id);
              } catch (error) {
                notification.error(
                  "Unable to approve Monthly Coverage Plan. Please try again.",
                );
              }
            },
          },
          cancel: { text: "No" },
        },
      });
    },
    [id, navigate],
  );

  const validate = (form: any) => {
    const data = form.formValues as ScheduleSiteChangeRequestDto;
    const errors: Record<string, string> = {};

    const payloadData = payload(data);

    const schedValidations = validateSchedules(payloadData.payload ?? []);
    if (schedValidations.length) {
      schedValidations.forEach((schedValidation) => {
        notification.info(schedValidation.message);
        errors.sched = "true";
      });
    }

    if (isEmpty(payloadData.coordinatorRemarks)) {
      notification.info("Remarks is required.");
      errors.remarks = "true";
    }

    if (!payloadData.areaId) {
      notification.info("Area is required.");
      errors.area = "true";
    }

    return errors;
  };

  const handleSubmit = useCallback(
    async (
      values: ScheduleSiteChangeRequestDto,
    ): Promise<ScheduleSiteChangeRequestDto> => {
      dialogBox.confirm({
        title: "Submit Schedule and Site Change Request?",
        content: (
          <>Are you sure you want to submit Schedule and Site Change Request?</>
        ),
        actions: {
          confirm: {
            text: "Yes.",
            onClick: async () => {
              try {
                await submitSiteScheduleChangeRequests(id ?? "", {
                  id: id ?? "",
                  supervisorRemarks: values.remarks,
                });
                notification.success(
                  "Schedule and Site Change Request submitted successfully.",
                );

                await loadData(id);
              } catch (error) {
                notification.error(
                  "Unable to submit Monthly Coverage Plan. Please try again.",
                );
              }
            },
          },
          cancel: { text: "No" },
        },
      });
      return values;
    },
    [id, navigate],
  );

  const handleRevise = useCallback(
    async (values: ScheduleSiteChangeRequestDto): Promise<void> => {
      dialogBox.confirm({
        title: "Set Schedule and Site Change Request to For Revision?",
        content: (
          <>
            Are you sure you want to set Schedule and Site Change Request to for
            revision?
          </>
        ),
        actions: {
          confirm: {
            text: "Yes.",
            onClick: async () => {
              try {
                await reviseSiteScheduleChangeRequests(id ?? "", {
                  id,
                  supervisorRemarks: values.supervisorRemarks,
                });
                notification.success(
                  "Schedule and Site Change Request is set to for revision successfully.",
                );

                await loadData(id);
              } catch (error) {
                notification.error(
                  "Unable to set Monthly Coverage Plan to for revision. Please try again.",
                );
              }
            },
          },
          cancel: { text: "No" },
        },
      });
    },
    [navigate],
  );

  const handleCancel = useCallback(
    async (values: ScheduleSiteChangeRequestDto): Promise<void> => {
      dialogBox.confirm({
        title: "Cancel Schedule and Site Change Request?",
        content: (
          <>Are you sure you want to cancel Schedule and Site Change Request?</>
        ),
        actions: {
          confirm: {
            text: "Yes.",
            onClick: async () => {
              try {
                await cancelSiteScheduleChangeRequests(id ?? "", {
                  id: id ?? "",
                });
                notification.success(
                  "Schedule and Site Change Request is cancelled successfully.",
                );

                await loadData(id);
              } catch (error) {
                notification.error(
                  "Unable cancel Monthly Coverage Plan. Please try again.",
                );
              }
            },
          },
          cancel: { text: "No" },
        },
      });
    },
    [navigate],
  );

  const areAssignmentsEqual = (
    current: ScheduleSiteChangeRequestAssignmentDto[],
    original: ScheduleSiteChangeRequestAssignmentDto[],
  ): boolean => {
    if (current.length !== original.length) {
      return false;
    }
    const normalize = (assignments: ScheduleSiteChangeRequestAssignmentDto[]) =>
      assignments
        .map((x) => ({
          id: x.id ?? null,
          monthlyCoveragePlanId: x.monthlyCoveragePlanId ?? null,
          date: x.date?.getTime() ?? null,
          branchId: x.branchId ?? null,
          fromSchedule: x.fromSchedule?.getTime() ?? null,
          toSchedule: x.toSchedule?.getTime() ?? null,
          isDeleted: x.isDeleted ?? false,
        }))
        .sort((a, b) => String(a.id ?? "").localeCompare(String(b.id ?? "")));
    return (
      JSON.stringify(normalize(current)) === JSON.stringify(normalize(original))
    );
  };

  const back = useCallback(
    (values: ScheduleSiteChangeRequestDto) => {
      const currentAssignments = values.payload ?? [];
      const originalAssignments = initialAssignmentsRef.current;
      const assignmentsChanged = !areAssignmentsEqual(
        currentAssignments,
        originalAssignments,
      );

      const hasNewChanges =
        Object.keys(formRef.current?.formState?.dirtyFields ?? {}).length > 0 ||
        assignmentsChanged;

      if (!formRef.current) return;

      const proceedBack = () => navigate({ to: "/deployment/schedule-change" });

      if (hasNewChanges) {
        dialogBox.confirm({
          title: "Leave without saving?",
          content: (
            <>You have unsaved changes. Are you sure you want to leave?</>
          ),
          actions: {
            confirm: { text: "Yes, leave", onClick: proceedBack },
            cancel: { text: "No" },
          },
        });
      } else {
        proceedBack();
      }
    },
    [navigate],
  );

  return (
    <ContentLayout>
      <ContentHeader
        title={`${capitalCase(id ? "display" : "create")} Schedule and Site Change Request`}
        breadcrumb
        loading={loading}
        back
      />
      <Form
        ref={formRef}
        onSubmit={handleSubmit}
        validate={validate}
        values={initialValues}
        reValidateMode="onSubmit"
      >
        {({ control, getValues }) => {
          const sscrSiteAssignmentDetailsColumns: ControlledDataGridColumnProps<ScheduleSiteChangeRequestAssignmentDto>[] =
            [
              {
                field: "date",
                title: "Date",
                editor: "date",
                format: "{0:MM/dd/yyyy}",
                editorProps: {
                  min: minSchedDateRef.current?.toJSDate(),
                  max: maxSchedDateRef.current?.toJSDate(),
                },
                display: ({ value }: any) => (
                  <span>{value ? formatDate(value, "MM/dd/yyyy") : ""}</span>
                ),
              },
              {
                field: "branchId",
                title: "Location / Branch",
                editor: "combobox",
                editorProps: {
                  data: branchOptions,
                  dataItemKey: "id",
                  textField: "name",
                },
              },
              {
                field: "fromSchedule",
                title: "Schedule Time From",
                format: "{0:hh:mm a}",
                editor: "time",
                editorProps: (dataItem: any) => {
                  const { toSchedule, date } = dataItem.dataItem;

                  let maxDate: Date | undefined;

                  if (toSchedule) {
                    maxDate = new Date(toSchedule);
                    maxDate.setMinutes(maxDate.getMinutes() - 15);

                    if (date) {
                      const scheduleDate = new Date(date);

                      maxDate.setFullYear(
                        scheduleDate.getFullYear(),
                        scheduleDate.getMonth(),
                        scheduleDate.getDate(),
                      );
                      maxDate.setSeconds(0, 0);
                    }
                  }

                  return {
                    format: "hh:mm a",
                    steps: { hour: 0, minute: 15, second: 0 },
                    max: maxDate,
                    inputAttributes: {
                      readOnly: true,
                    },
                  };
                },
                display: ({ value }: any) => (
                  <span>{value ? formatDate(value, "hh:mm a") : ""}</span>
                ),
              },
              {
                field: "toSchedule",
                title: "Schedule Time To",
                format: "{0:hh:mm a}",
                editor: "time",
                editorProps: (dataItem: any) => {
                  const { fromSchedule, date } = dataItem.dataItem;

                  let minDate: Date | undefined;

                  if (fromSchedule) {
                    minDate = new Date(fromSchedule);
                    minDate.setMinutes(minDate.getMinutes() + 15);

                    if (date) {
                      const scheduleDate = new Date(date);

                      minDate.setFullYear(
                        scheduleDate.getFullYear(),
                        scheduleDate.getMonth(),
                        scheduleDate.getDate(),
                      );
                      minDate.setSeconds(0, 0);
                    }
                  }

                  return {
                    format: "hh:mm a",
                    steps: { hour: 0, minute: 15, second: 0 },
                    min: minDate,
                    inputAttributes: {
                      readOnly: true,
                    },
                  };
                },
                display: ({ value }: any) => (
                  <span>{value ? formatDate(value, "hh:mm a") : ""}</span>
                ),
              },
            ];

          return (
            <>
              <FormSection title="Header" loading={loading}>
                <FormFieldSet cols={2}>
                  <ControlledComboBox
                    control={control}
                    name="monthId"
                    label="Month"
                    data={MONTHS_OPTIONS}
                    dataItemKey="id"
                    textField="code"
                    readOnly
                  />
                  <ControlledComboBox
                    control={control}
                    name="year"
                    label="Year"
                    data={YEARS_OPTIONS}
                    dataItemKey="id"
                    textField="code"
                    readOnly
                  />
                  <ControlledInput
                    control={control}
                    name="coordinatorName"
                    label="Coordinator Name"
                    readOnly
                  />
                  {areaOptionsData && (
                    <ControlledComboBox
                      control={control}
                      name="areaId"
                      label="Area"
                      data={areaOptionsData}
                      dataItemKey="id"
                      textField="name"
                      readOnly
                      required={!isDisplayOnly}
                    />
                  )}
                  <ControlledDatePicker
                    control={control}
                    name="dateFrom"
                    label="Date From"
                    format={"MM/dd/yyyy"}
                    readOnly
                  />
                  <ControlledDatePicker
                    control={control}
                    name="dateTo"
                    label="Date To"
                    format={"MM/dd/yyyy"}
                    readOnly
                  />
                  {statusOptions && (
                    <ControlledComboBox
                      control={control}
                      name="statusId"
                      label="Status"
                      data={statusOptions}
                      dataItemKey="id"
                      textField="name"
                      readOnly
                    />
                  )}
                </FormFieldSet>
              </FormSection>
              <FormSection
                title={`Site Assignments${isDisplayOnly ? "" : "*"}`}
              >
                <FormFieldSet>
                  <ControlledDataGrid
                    fieldArrayName="payload"
                    columns={sscrSiteAssignmentDetailsColumns}
                    readOnly={isDisplayOnly}
                    canAdd={!isDisplayOnly}
                    commands={isDisplayOnly ? [] : undefined}
                    control={control}
                    dataItemKey="id"
                    loading={loading}
                  />
                </FormFieldSet>
              </FormSection>
              <FormSection>
                <FormFieldSet>
                  <ControlledTextArea
                    name="coordinatorRemarks"
                    control={control}
                    loading={loading}
                    label="Remarks"
                    readOnly={isDisplayOnly}
                    required={!isDisplayOnly}
                  />
                </FormFieldSet>
              </FormSection>
              <FormActionsBar loading={loading}>
                {!actionAvailability(DEPLOYMENT_ACTIONS.Save, getValues()) && (
                  <Button
                    type="button"
                    svgIcon={saveIcon}
                    themeColor="primary"
                    onClick={() => handleSave(getValues())}
                  >
                    Save
                  </Button>
                )}
                {!actionAvailability(DEPLOYMENT_ACTIONS.Edit, getValues()) && (
                  <Button
                    type="button"
                    svgIcon={pencilIcon}
                    themeColor="base"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate({
                        to: "/deployment/schedule-change/$id/edit",
                        params: { id: id! },
                      });
                    }}
                  >
                    Edit
                  </Button>
                )}
                {!actionAvailability(
                  DEPLOYMENT_ACTIONS.Submit,
                  getValues(),
                ) && (
                  <Button
                    type="submit"
                    fillMode="outline"
                    svgIcon={checkIcon}
                    themeColor="secondary"
                  >
                    Submit
                  </Button>
                )}
                {!actionAvailability(
                  DEPLOYMENT_ACTIONS.ForRevision,
                  getValues(),
                ) && (
                  <Button
                    type="button"
                    fillMode="outline"
                    svgIcon={undoIcon}
                    themeColor="secondary"
                    onClick={() => handleRevise(getValues())}
                  >
                    For Revision
                  </Button>
                )}
                {!actionAvailability(
                  DEPLOYMENT_ACTIONS.Approve,
                  getValues(),
                ) && (
                  <Button
                    type="button"
                    fillMode="outline"
                    svgIcon={checkIcon}
                    themeColor="secondary"
                    onClick={() => handleApprove(getValues())}
                  >
                    Approve
                  </Button>
                )}
                {!actionAvailability(
                  DEPLOYMENT_ACTIONS.Cancel,
                  getValues(),
                ) && (
                  <Button
                    type="button"
                    fillMode="outline"
                    svgIcon={undoIcon}
                    themeColor="secondary"
                    onClick={() => handleCancel(getValues())}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="button"
                  fillMode="outline"
                  svgIcon={cancelIcon}
                  themeColor="secondary"
                  onClick={() => back(getValues())}
                >
                  Back
                </Button>
              </FormActionsBar>
            </>
          );
        }}
      </Form>
    </ContentLayout>
  );
};

export default ScheduleAndSiteChangeRequestForm;
